import type { CodeableConcept, ContactPoint } from "./common";
import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";

/** Stable Location domain identity, independent of the FHIR Location locator. */
export type LocationId = DomainId<"LocationId">;

/** Boundary for identities restored from persistence or accepted by contracts. */
export function asLocationId(value: string): LocationId {
  if (!value) throw new PreconditionFailedError("A Location identity cannot be empty");
  return value as LocationId;
}

// ============================================================
// Location — a place care happens in (ADR-0017)
//
// Like a device, a location is a schedulable RESOURCE: rooms hold calendars.
// It knows NOTHING about them, for the same reason [[device.model]] does not:
// a Schedule is its own aggregate, and a count kept here made a room answer for
// one. Registration opens the calendar and deregistration closes it, both in
// one session, so the two stay in step without either holding the other's facts.
// ============================================================

type LocationStatus = "active" | "suspended" | "inactive";

interface Address {
  line?: string[];
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface HoursOfOperation {
  daysOfWeek?: string[];
  allDay?: boolean;
  openingTime?: string;
  closingTime?: string;
}

interface LocationFields {
  name: string;
  status?: LocationStatus;
  description?: string;
  type?: CodeableConcept[];
  physicalType?: CodeableConcept;
  address?: Address;
  telecom?: ContactPoint[];
  hoursOfOperation?: HoursOfOperation[];
  timezone?: string;
  /** The location this one sits inside — a room within a clinic. */
  partOfId?: LocationId;
}

export interface CreateLocationInput extends LocationFields {
  id: string;
}

export interface LocationProps extends LocationFields {
  id: LocationId;
}

export class Location {
  private state: LocationProps;

  private constructor(props: LocationProps) {
    if (!props.name) {
      throw new PreconditionFailedError("Location requires a name");
    }
    if (props.id && props.partOfId === props.id) {
      throw new PreconditionFailedError("A location cannot be part of itself");
    }
    this.state = structuredClone(props);
  }

  // ── facts ──

  static create(input: CreateLocationInput): Location {
    return new Location({ ...structuredClone(input), id: asLocationId(input.id) });
  }

  static restore(snapshot: LocationProps): Location {
    return new Location(snapshot);
  }

  get id(): LocationId {
    return this.state.id;
  }

  get isActive(): boolean {
    return this.state.status !== "inactive";
  }

  snapshot(): LocationProps {
    return structuredClone(this.state);
  }

  // ── operations ──

  /**
   * Take it out of service. Closing the calendar is the caller's other half of
   * the same session — whether it MAY close is Schedule's own rule, since only
   * that aggregate knows what is booked on it.
   */
  deregister(): void {
    this.state.status = "inactive";
  }

  /**
   * Correct the location's own details. A key that is present is applied even
   * when its value is `undefined` — that is how a caller clears a field —
   * which is why this takes an explicit change set rather than a merge of a
   * whole snapshot. `id` is not revisable: identity is not editable.
   */
  revise(changes: Partial<Omit<LocationProps, "id">>): void {
    if (changes.name !== undefined && !changes.name) {
      throw new PreconditionFailedError("Location requires a name");
    }
    if (changes.partOfId !== undefined && changes.partOfId === this.state.id) {
      throw new PreconditionFailedError("A location cannot be part of itself");
    }
    Object.assign(this.state, changes);
  }

  setTimezone(timezone: string): void {
    if (!timezone) {
      throw new PreconditionFailedError("Timezone is required");
    }
    this.state.timezone = timezone;
  }
}
